import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetTransportLeadTime } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  hrs,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/**
 * Snitt för bilar på transport — ledtid för inköps- och inbytesbilar från första upphämtningen
 * till avlämningen i Strängnäs, per startlän.
 *
 * Median och längsta ledtid står bredvid snittet med flit. Ett län kan ha tre bilar i veckan,
 * och då är snittet en enskild bils historia. "Varav körtid" står där av samma skäl: skillnaden
 * mot ledtiden är väntetid, och det är den som går att åtgärda.
 */
const methodNoteStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#666',
  marginTop: '10px',
  marginBottom: 0,
  lineHeight: 1.5,
};

const TransportLeadTimeWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetTransportLeadTime();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  // Datapunkterna kommer per period och län. Widgeten visar länet över hela det valda
  // intervallet, så perioderna vägs ihop — snitt och median viktas på antal bilar, och
  // längsta ledtid är den längsta i någon av perioderna.
  const byCounty = new Map<
    string,
    { cars: number; leadSum: number; activeSum: number; max: number; medians: number[] }
  >();
  (result?.dataPoints ?? []).forEach((dp) => {
    const current = byCounty.get(dp.fromCountyName) ?? {
      cars: 0,
      leadSum: 0,
      activeSum: 0,
      max: 0,
      medians: [],
    };
    current.cars += dp.cars;
    current.leadSum += dp.averageHours * dp.cars;
    current.activeSum += dp.activeHours * dp.cars;
    current.max = Math.max(current.max, dp.maxHours);
    current.medians.push(dp.medianHours);
    byCounty.set(dp.fromCountyName, current);
  });

  const counties = Array.from(byCounty.entries())
    .map(([name, v]) => ({
      name,
      cars: v.cars,
      average: v.cars > 0 ? v.leadSum / v.cars : 0,
      active: v.cars > 0 ? v.activeSum / v.cars : 0,
      max: v.max,
      // Med flera perioder i intervallet går den exakta medianen inte att räkna ut ur
      // aggregaten. Visas bara när det finns en enda period, hellre än att gissa fram ett
      // tal som ser exakt ut.
      median: v.medians.length === 1 ? v.medians[0] : null,
    }))
    .sort((a, b) => b.cars - a.cars);

  const byCategory = new Map<string, { cars: number; leadSum: number }>();
  (result?.byCategory ?? []).forEach((dp) => {
    const current = byCategory.get(dp.categoryName) ?? { cars: 0, leadSum: 0 };
    current.cars += dp.cars;
    current.leadSum += dp.averageHours * dp.cars;
    byCategory.set(dp.categoryName, current);
  });
  const categories = Array.from(byCategory.entries())
    .map(([name, v]) => ({
      name,
      cars: v.cars,
      average: v.cars > 0 ? v.leadSum / v.cars : 0,
    }))
    .sort((a, b) => b.cars - a.cars);

  const facilityMissing = result != null && !result.strangnasFacilityResolved;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.TransportLeadTime]}
      count={Math.round(result?.averageHours ?? 0)}
      isLoading={getData.isLoading}
      info="Ledtid i klocktid för inköps- och inbytesbilar, från första upphämtningen till avlämningen på logistikanläggningen i Strängnäs, uppdelat per startlän. Helger och nätter räknas med — bilen är låst även då. Auktion redovisas som inköp, som i Levererade bilar. Bilar som åkt på lastbil mäts på lastbilens last- och losstider; saknas förarens markering i ZäkraGo blir ledtiden ungefärlig, och en bil utan mätbar tid räknas som en lucka i underlaget i stället för som ledtid noll."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!facilityMissing && counties.length === 0}
      >
        {facilityMissing ? (
          <p style={{ color: '#a33', margin: 0 }}>
            Logistikanläggningen i Strängnäs kunde inte hittas bland företagen. Talet går
            inte att räkna fram — det betyder alltså inte att inga bilar kört. Åtgärden är att
            märka anläggningen som logistikcenter.
          </p>
        ) : (
          <>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Startlän</th>
                  <th style={thNumStyle}>Bilar</th>
                  <th style={thNumStyle}>Snitt</th>
                  <th style={thNumStyle}>Median</th>
                  <th style={thNumStyle}>Längsta</th>
                  <th style={thNumStyle}>Varav körtid</th>
                </tr>
              </thead>
              <tbody>
                {counties.map((county) => (
                  <tr key={county.name} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={tdStyle}>{county.name}</td>
                    <td style={tdNumStyle}>{nf(county.cars)}</td>
                    <td style={tdNumStyle}>{hrs(county.average)}</td>
                    <td style={tdNumStyle}>{hrs(county.median)}</td>
                    <td style={tdNumStyle}>{hrs(county.max)}</td>
                    <td style={tdNumStyle}>{hrs(county.active)}</td>
                  </tr>
                ))}
                <tr style={totalRowStyle}>
                  <td style={tdStyle}>Totalt</td>
                  <td style={tdNumStyle}>{nf(result?.cars ?? 0)}</td>
                  <td style={tdNumStyle}>{hrs(result?.averageHours)}</td>
                  <td style={tdNumStyle}>{hrs(result?.medianHours)}</td>
                  <td style={tdNumStyle}>{hrs(result?.maxHours)}</td>
                  <td style={tdNumStyle}>{hrs(result?.activeHours)}</td>
                </tr>
              </tbody>
            </table>

            {categories.length > 0 && (
              <>
                <h3 style={{ fontSize: '14px', margin: '18px 0 8px' }}>Per ärendetyp</h3>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Ärendetyp</th>
                      <th style={thNumStyle}>Bilar</th>
                      <th style={thNumStyle}>Snitt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category.name} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={tdStyle}>{category.name}</td>
                        <td style={tdNumStyle}>{nf(category.cars)}</td>
                        <td style={tdNumStyle}>{hrs(category.average)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {(result?.truckCarsMeasuredByFallback ?? 0) > 0 && (
              <p style={methodNoteStyle}>
                <strong>Om lastbilstransporterade bilar.</strong> Klockan startar när bilen hämtas
                och stannar när den lämnas i Strängnäs. För en bil som åkt på lastbil hämtas de
                tiderna från lastbilen, eftersom uppdragets egna tider skrivs om i lastningen.
                Bästa källan är förarens egen last- och lossmarkering i ZäkraGo. Saknas den
                används segmentets eller lastbilsuppdragets tider, som mäter lastbilens rörelse
                och inte den enskilda bilens — ledtiden blir då ungefärlig.{' '}
                {nf(result?.truckCarsMeasuredByFallback ?? 0)} av{' '}
                {nf(
                  (result?.truckCarsMeasuredByDriverMarking ?? 0) +
                    (result?.truckCarsMeasuredByFallback ?? 0)
                )}{' '}
                lastbilsbilar i perioden vilar på den grövre källan. Talet är därför försiktigt
                mätt men inte exakt, och korrigeras i takt med att fler lastbilsförare markerar
                lastning och lossning i appen.
              </p>
            )}

            <ControlFigures
              items={[
                result && result.casesWithoutStrangnasDropOff > 0
                  ? `${nf(result.casesWithoutStrangnasDropOff)} inköps-/inbytesärenden saknar en godkänd etapp som slutar i Strängnäs — de ingår inte i snittet`
                  : null,
                result && result.casesWithoutUsableTimes > 0
                  ? `${nf(result.casesWithoutUsableTimes)} ärenden saknar användbara start- eller sluttider`
                  : null,
                result && result.carsWithoutMeasurableTime > 0
                  ? `${nf(result.carsWithoutMeasurableTime)} bilar saknar mätbar tid (spann under ${nf(result.minimumMeasurableMinutes ?? 3)} min) och redovisas inte som ledtid noll`
                  : null,
                result && result.carsWithoutFromCounty > 0
                  ? `${nf(result.carsWithoutFromCounty)} bilar saknar frånlän och ligger på raden "(Okänt län)"`
                  : null,
              ]}
            />
          </>
        )}
      </WidgetState>
    </Widget>
  );
};

export default TransportLeadTimeWidget;
